import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, API } from "@/lib/api";
import { useSession } from "@/lib/session";
import { LOCATIONS, LOCATIONS_GR } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Lock, Folder, FolderOpen, Receipt, FileText, Banknote, CalendarDays, Download, Eye,
} from "lucide-react";
import AIAnalysisPanel from "@/components/AIAnalysisPanel";
import TaxMatrixTable from "@/components/TaxMatrixTable";
import OwnerAuditAlerts from "@/components/OwnerAuditAlerts";
import SubscriptionManagement from "@/components/SubscriptionManagement";
import WhitelabelSettings from "@/components/WhitelabelSettings";
import WeeklyLiabilities from "@/components/WeeklyLiabilities";
import ImageLightbox from "@/components/ImageLightbox";
import PortalExtras from "@/components/PortalExtras";

export default function PrivatePortal() {
  const { portalUnlocked, unlockPortal, lockPortal } = useSession();
  const [auth, setAuth] = useState({ username: "", password: "" });
  const [activeStore, setActiveStore] = useState(null);
  const [folderDates, setFolderDates] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [cheques, setCheques] = useState([]);
  const [downloading, setDownloading] = useState(false);
  // Visual Media Audit Vault — owner-only lightbox + per-supplier gallery
  const [lightbox, setLightbox] = useState({ src: null, caption: "" });

  // Iter 115 · Row-level delete for the Contents panel (scans / invoices / cheques).
  // Confirms in Greek, calls the matching DELETE endpoint, then filters the
  // bundle state locally so the row vanishes without a full reload.
  const deleteContentRow = async (kind, id, label) => {
    const kindLabel =
      kind === "scan" ? "τη σάρωση" :
      kind === "invoice" ? "το τιμολόγιο" :
      kind === "cheque" ? "την επιταγή" : "το αρχείο";
    if (!window.confirm(`Διαγραφή; · Delete ${kindLabel} "${label || id}"?`)) return;
    const url =
      kind === "scan" ? `/scans/${id}` :
      kind === "cheque" ? `/cheques/${id}` :
      `/invoices/${id}`;
    try {
      await api.delete(url);
      setBundle((b) => {
        if (!b) return b;
        if (kind === "scan") {
          return { ...b, endshift: { ...b.endshift, scans: b.endshift.scans.filter((s) => s.id !== id) } };
        }
        if (kind === "invoice") {
          return { ...b, invoices: b.invoices.filter((i) => i.id !== id) };
        }
        if (kind === "cheque") {
          return { ...b, cheques: b.cheques.filter((c) => c.id !== id) };
        }
        return b;
      });
      toast.success("Διαγράφηκε · Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const submit = async () => {
    try {
      await api.post("/portal/auth", {
        username: (auth.username || "").trim().toLowerCase(),
        password: (auth.password || "").trim().toLowerCase(),
      });
      unlockPortal();
      toast.success("Welcome to KioskForce ERP");
    } catch {
      toast.error("Invalid credentials");
    }
  };

  const downloadBackup = async () => {
    setDownloading(true);
    const t = toast.loading("Compiling full database backup…");
    try {
      const resp = await fetch(`${API}/portal/backup`, {
        method: "GET",
        // Owner-only gate — server verifies these creds before streaming.
        headers: { Authorization: "Bearer savvas:savvas" },
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      // Honour the server's Content-Disposition filename so the new
      // hierarchical root folder name lands intact in the user's Downloads.
      const today = new Date().toISOString().slice(0, 10);
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `Kiosk_ERP_Backup_${today}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup saved · ${filename}`, { id: t });
    } catch (e) {
      toast.error(e?.message || "Backup failed", { id: t });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!portalUnlocked) return;
    api.get("/cheques").then((r) => setCheques(r.data || [])).catch(() => {});
  }, [portalUnlocked]);

  const openStore = async (store) => {
    setActiveStore(store);
    setActiveDate(null);
    setBundle(null);
    try {
      const { data } = await api.get(`/portal/folders/${store}`);
      setFolderDates(data.dates || []);
    } catch {
      setFolderDates([]);
    }
  };

  const openDate = async (date) => {
    setActiveDate(date);
    try {
      const { data } = await api.get(`/portal/folders/${activeStore}/${date}`);
      setBundle(data);
    } catch {
      setBundle(null);
    }
  };

  if (!portalUnlocked) {
    return (
      <Card className="max-w-md mx-auto mt-16 p-8" data-testid="portal-gate">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="h-6 w-6 text-slate-900" />
          <h2 className="text-xl font-bold text-slate-900">Private Portal</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">Restricted Master Financial Center.</p>
        <Input
          placeholder="Username"
          value={auth.username}
          onChange={(e) => setAuth({ ...auth, username: e.target.value })}
          className="mb-3 min-h-[52px]"
          data-testid="portal-username"
        />
        <Input
          type="password"
          placeholder="Password"
          value={auth.password}
          onChange={(e) => setAuth({ ...auth, password: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="mb-4 min-h-[52px]"
          data-testid="portal-password"
        />
        <Button onClick={submit} className="w-full min-h-[56px] bg-slate-900 hover:bg-slate-800 font-bold" data-testid="portal-submit-btn">
          Unlock
        </Button>
      </Card>
    );
  }

  // Build colour-coded buckets of cheque maturity dates for the calendar.
  // Each cheque carries a `credit_color` (red ≤ 15d, amber 16-45d, green ≥ 46d)
  // populated by the backend credit-bucket logic.
  const _parseMat = (m) => { try { return new Date(m); } catch { return null; } };
  const redDates = cheques.filter((c) => c.credit_color === "red" && c.maturity).map((c) => _parseMat(c.maturity)).filter(Boolean);
  const amberDates = cheques.filter((c) => c.credit_color === "amber" && c.maturity).map((c) => _parseMat(c.maturity)).filter(Boolean);
  const greenDates = cheques.filter((c) => c.credit_color === "green" && c.maturity).map((c) => _parseMat(c.maturity)).filter(Boolean);
  const greyDates = cheques.filter((c) => (!c.credit_color || c.credit_color === "grey") && c.maturity).map((c) => _parseMat(c.maturity)).filter(Boolean);
  const chequeDates = [...redDates, ...amberDates, ...greenDates, ...greyDates];

  return (
    <div>
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Private Portal</h1>
          <p className="text-slate-500 mt-1 tracking-wide">Κέντρο Διαχείρισης · Owner only</p>
        </div>
        <Button variant="outline" onClick={lockPortal} data-testid="portal-lock">Lock</Button>
      </header>

      <AIAnalysisPanel />

      <TaxMatrixTable />

      {/* Iter 36 — Infrastructure config (Wolt/Bolt/Foody webhooks + Live
          Camera RTSP/HTTPS URLs) was extracted out of the Private Portal
          into the SETTINGS tab. The Portal is now a pure executive
          financial dashboard (Tax Matrix · Cheque Calendar · OpEx · Backup).
          To manage cameras/delivery webhooks: TOP NAV → SETTINGS. */}

      <OwnerAuditAlerts />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5" data-testid="portal-saas-cards">
        <SubscriptionManagement />
        <WhitelabelSettings />
      </div>

      {/* ----------------------------------------------------------------
          Owner-only · Full Database Backup. The Portal route is already
          gated by `savvas/savvas` so this card never renders for managers
          or employees who haven't unlocked the Portal.
      ---------------------------------------------------------------- */}
      <Card className="p-5 mb-6 border-l-4 border-l-slate-900" data-testid="owner-backup-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Download className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg leading-tight">Full Database Backup</h3>
              <p className="text-[11px] text-slate-500 tracking-wide">
                Πλήρες αντίγραφο ασφαλείας · Owner only · All 4 kiosks · All collections
              </p>
            </div>
          </div>
          <Button
            onClick={downloadBackup}
            disabled={downloading}
            data-testid="download-backup-btn"
            className="min-h-[56px] px-6 bg-slate-900 hover:bg-slate-800 text-white font-bold tracking-wide gap-2"
          >
            <Download className="h-5 w-5" />
            {downloading ? "Compiling…" : "Download Full Database Backup"}
          </Button>
        </div>
        <p
          className="text-xs text-slate-500 mt-3 leading-snug"
          data-testid="backup-instructions"
        >
          Once downloaded, you can safely copy and paste this backup file into your external
          USB drive for permanent offline storage. The .zip is structured as{" "}
          <code>Kiosk_ERP_Backup_&lt;date&gt;/&lt;Kiosk&gt;/&lt;Supplier-or-Category&gt;/</code>{" "}
          — Python creates a fresh subfolder on-the-fly for each supplier or category
          encountered (e.g. <code>Coca-Cola/</code>, <code>SalaryAdvance/</code>,{" "}
          <code>Plumbing_Repairs/</code>). Z-Reports + VISA slips land in{" "}
          <code>Z-Reports/</code> and anything with a blank / unreadable category falls
          back into <code>Uncategorized_Receipts/</code>. A hyperlinked{" "}
          <code>master.xlsx</code> sits at the root with a clickable 📷 column on each row.
        </p>
      </Card>

      <Tabs defaultValue="folders" data-testid="portal-tabs">
        <TabsList className="mb-6">
          <TabsTrigger value="folders" data-testid="portal-tab-folders">Store Folders</TabsTrigger>
          <TabsTrigger value="cheques" data-testid="portal-tab-cheques">Master Cheque Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="folders">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {LOCATIONS.map((loc) => (
              <button
                key={loc}
                data-testid={`folder-${loc.toLowerCase()}`}
                onClick={() => openStore(loc)}
                className={`p-5 rounded-xl border-2 text-left transition-colors ${
                  activeStore === loc ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900 hover:border-slate-400"
                }`}
              >
                <div className="flex items-center gap-2">
                  {activeStore === loc ? <FolderOpen className="h-5 w-5" /> : <Folder className="h-5 w-5" />}
                  <div className="font-bold text-lg">{loc}</div>
                </div>
                <div className="text-xs opacity-70 mt-0.5">{LOCATIONS_GR[loc]}</div>
              </button>
            ))}
          </div>

          {activeStore && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card className="p-5 lg:col-span-1">
                <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">
                  Daily Folders · {activeStore}
                </h3>
                {folderDates.length === 0 ? (
                  <p className="text-slate-500 text-sm">No data yet for this store.</p>
                ) : (
                  <div className="space-y-1 max-h-[460px] overflow-y-auto">
                    {folderDates.map((d) => (
                      <button
                        key={d}
                        onClick={() => openDate(d)}
                        data-testid={`folder-date-${d}`}
                        className={`w-full text-left px-3 py-2 rounded-lg font-mono text-sm flex items-center gap-2 ${
                          activeDate === d ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"
                        }`}
                      >
                        <CalendarDays className="h-4 w-4" />
                        {d}
                      </button>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="p-5 lg:col-span-2">
                <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">
                  {activeDate ? `Contents · ${activeDate}` : "Pick a date"}
                </h3>
                {!bundle && (
                  <div className="text-slate-400 text-sm">Select a daily folder from the left.</div>
                )}
                {bundle && (
                  <div className="space-y-5">
                    <Bucket
                      title="/endshift"
                      icon={<Banknote className="h-4 w-4" />}
                      empty={bundle.endshift.shifts.length === 0 && bundle.endshift.scans.length === 0}
                      testId="bucket-endshift"
                    >
                      {bundle.endshift.shifts.map((s) => (
                        <div key={s.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm" data-testid={`endshift-${s.id}`}>
                          <div className="font-semibold">{s.employee_name} · R{s.register}</div>
                          {s.end_data && (
                            <div className="text-xs font-mono text-slate-600 mt-1">
                              Expected €{s.end_data.expected_cash?.toFixed?.(2)} · Counted €{s.end_data.counted_cash?.toFixed?.(2)} · Δ {s.end_data.discrepancy >= 0 ? "+" : ""}€{s.end_data.discrepancy?.toFixed?.(2)}
                            </div>
                          )}
                        </div>
                      ))}
                      {bundle.endshift.scans.map((s) => (
                        <div key={s.id} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-2">
                          <div className="text-xs font-bold uppercase text-slate-500 w-16">{s.kind}</div>
                          {s.image_url ? (
                            <button
                              onClick={() => setLightbox({
                                src: s.image_url,
                                caption: `${s.kind} · ${(s.created_at || "").slice(0, 16)}`,
                              })}
                              className="relative shrink-0 group"
                              data-testid={`scan-image-${s.id}`}
                              title="View original scan"
                            >
                              <img src={fileUrl(s.image_url)} alt="" className="h-10 w-10 object-cover rounded border" />
                              <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded">
                                <Eye className="h-4 w-4" />
                              </span>
                            </button>
                          ) : (
                            <div className="h-10 w-10 rounded border border-dashed border-slate-300 shrink-0" />
                          )}
                          <pre className="text-xs flex-1 overflow-hidden truncate text-slate-600">{JSON.stringify(s.data)}</pre>
                          <button
                            type="button"
                            onClick={() => deleteContentRow("scan", s.id, s.kind)}
                            data-testid={`scan-delete-${s.id}`}
                            title="Διαγραφή · Delete"
                            className="shrink-0 inline-flex items-center rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-100"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </Bucket>

                    <Bucket
                      title="/invoices"
                      icon={<FileText className="h-4 w-4" />}
                      empty={bundle.invoices.length === 0 && (bundle.shift_invoices || []).length === 0}
                      testId="bucket-invoices"
                    >
                      {bundle.invoices.map((inv) => (
                        <div key={inv.id} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid={`invoice-row-${inv.id}`}>
                          {inv.invoice_image_url ? (
                            <button
                              onClick={() => setLightbox({
                                src: inv.invoice_image_url,
                                caption: `${inv.supplier_name || "Invoice"} · ${inv.invoice_number || "—"} · €${(inv.total_amount || 0).toFixed(2)}`,
                              })}
                              className="relative shrink-0 group"
                              data-testid={`invoice-image-${inv.id}`}
                              title="View original photo"
                            >
                              <img src={fileUrl(inv.invoice_image_url)} alt="" className="h-10 w-10 object-cover rounded border" />
                              <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded">
                                <Eye className="h-4 w-4" />
                              </span>
                            </button>
                          ) : (
                            <div className="h-10 w-10 rounded border border-dashed border-slate-300 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate">{inv.supplier_name || "—"}</div>
                            <div className="text-xs font-mono text-slate-500 truncate">
                              {inv.invoice_number || "—"} · VAT {inv.vat_rate}%
                              {inv.payment_method === "VISA" && inv.payment_ref && (
                                <span className="ml-1 text-blue-700">· Ref {inv.payment_ref}</span>
                              )}
                            </div>
                          </div>
                          <div className="font-mono font-bold">€{inv.total_amount.toFixed(2)}</div>
                          <Badge className="text-[10px]">{inv.status.replace("_", " · ")}</Badge>
                          <button
                            type="button"
                            onClick={() => deleteContentRow("invoice", inv.id, inv.supplier_name || inv.invoice_number)}
                            data-testid={`invoice-delete-${inv.id}`}
                            title="Διαγραφή τιμολογίου · Delete"
                            className="shrink-0 inline-flex items-center rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-100"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                      {(bundle.shift_invoices || []).length > 0 && (
                        <div className="pt-2">
                          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">
                            Priced products · {bundle.shift_invoices.length}
                          </div>
                          <div className="space-y-1">
                            {bundle.shift_invoices.map((it) => (
                              <div
                                key={it.id}
                                className={`flex items-center gap-2 border rounded-lg px-2.5 py-1.5 text-xs ${
                                  it.source === "bulk_ocr"
                                    ? "bg-amber-50 border-amber-200"
                                    : "bg-white border-slate-200"
                                }`}
                                data-testid={`portal-shift-invoice-${it.id}`}
                              >
                                <span className="font-semibold text-slate-900 truncate flex-1">
                                  {it.source === "bulk_ocr" && (
                                    <span className="inline-block text-[9px] font-bold text-amber-700 bg-amber-100 px-1 rounded mr-1.5">AI</span>
                                  )}
                                  {it.product_name}
                                </span>
                                <span className="font-mono text-slate-600">Cost €{it.cost_ex_vat.toFixed(2)}</span>
                                <span className="font-mono text-slate-400">·</span>
                                <span className="font-mono text-slate-600">VAT {it.vat_rate}%</span>
                                <span className="font-mono text-slate-400">·</span>
                                <span className="font-mono text-slate-600">M {it.margin_pct}%</span>
                                <span className="font-mono text-slate-400">→</span>
                                <span className="font-mono font-bold text-blue-700">€{it.retail_price.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Bucket>

                    <Bucket
                      title="/cheques"
                      icon={<Receipt className="h-4 w-4" />}
                      empty={bundle.cheques.length === 0}
                      testId="bucket-cheques"
                    >
                      {bundle.cheques.map((c) => (
                        <div key={c.id} className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2" data-testid={`cheque-row-${c.id}`}>
                          {c.image_url && <img src={fileUrl(c.image_url)} alt="" className="h-10 w-10 object-cover rounded border" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate" data-testid={`cheque-title-${c.id}`}>
                              {c.event_title || `#${c.cheque_number || "—"} · ${c.supplier_name}`}
                            </div>
                            <div className="text-xs font-mono text-yellow-800">Maturity: {c.maturity}</div>
                          </div>
                          <div className="font-mono font-bold">€{(c.amount || 0).toFixed(2)}</div>
                          <button
                            type="button"
                            onClick={() => deleteContentRow("cheque", c.id, c.event_title || c.cheque_number)}
                            data-testid={`cheque-delete-${c.id}`}
                            title="Διαγραφή επιταγής · Delete"
                            className="shrink-0 inline-flex items-center rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-100"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </Bucket>
                  </div>
                )}
              </Card>
            </div>
            </>
          )}

          <PortalExtras
            onOpenFolder={(s, d) => {
              if (!s) return;
              openStore(s).then(() => d && openDate(d));
            }}
          />
        </TabsContent>

        <TabsContent value="cheques">
          <WeeklyLiabilities refreshKey={cheques.length} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card className="p-5">
              <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Master Cheque Calendar</h3>
              <Calendar
                mode="multiple"
                selected={chequeDates}
                modifiers={{
                  redBucket: redDates,
                  amberBucket: amberDates,
                  greenBucket: greenDates,
                  greyBucket: greyDates,
                }}
                modifiersClassNames={{
                  redBucket:   "bg-red-500    text-white     font-bold rounded-md",
                  amberBucket: "bg-amber-500  text-amber-50  font-bold rounded-md",
                  greenBucket: "bg-emerald-500 text-emerald-50 font-bold rounded-md",
                  greyBucket:  "bg-slate-300  text-slate-800 font-bold rounded-md",
                }}
                className="rounded-md border"
              />
              <div className="text-xs text-slate-500 mt-3 flex flex-wrap gap-3" data-testid="calendar-legend">
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-500" />≤ 15 days · urgent</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-500" />~30 days · medium</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-500" />≥ 60 days · safe</span>
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Pending Cheques · {cheques.length}</h3>
              {cheques.length === 0 ? (
                <p className="text-slate-500 text-sm">No cheques tracked yet.</p>
              ) : (
                <div className="space-y-2 max-h-[460px] overflow-y-auto">
                  {cheques.map((c) => {
                    const colorMap = {
                      red: "bg-red-50 border-red-200 text-red-700",
                      amber: "bg-amber-50 border-amber-200 text-amber-700",
                      green: "bg-emerald-50 border-emerald-200 text-emerald-700",
                      grey: "bg-slate-50 border-slate-200 text-slate-700",
                    };
                    const cls = colorMap[c.credit_color] || colorMap.grey;
                    const badge = (
                      <Badge className={`text-[10px] font-bold border ${
                        c.credit_color === "red" ? "bg-red-100 text-red-800 border-red-200"
                        : c.credit_color === "amber" ? "bg-amber-100 text-amber-800 border-amber-200"
                        : c.credit_color === "green" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"
                      }`}>{c.credit_tag || "Pending"}</Badge>
                    );
                    return (
                      <div key={c.id} className={`flex items-center gap-3 border rounded-lg p-2 ${cls}`} data-testid={`master-cheque-${c.id}`}>
                        {c.image_url ? (
                          <button
                            onClick={() => setLightbox({
                              src: c.image_url,
                              caption: c.event_title || `${c.supplier_name || ""} · ${c.cheque_number || ""}`,
                            })}
                            className="relative shrink-0 group"
                            data-testid={`cheque-image-${c.id}`}
                            title="View cheque photo"
                          >
                            <img src={fileUrl(c.image_url)} alt="" className="h-10 w-10 object-cover rounded border" />
                            <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded">
                              <Eye className="h-4 w-4" />
                            </span>
                          </button>
                        ) : (
                          <div className="h-10 w-10 rounded border border-dashed border-slate-300 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate" data-testid={`master-cheque-title-${c.id}`}>
                            {c.event_title || `#${c.cheque_number || "—"} · ${c.supplier_name || "—"}`}
                          </div>
                          <div className="text-xs font-mono mt-0.5 flex items-center gap-2">
                            <span className="opacity-75">{c.location} · Matures {c.maturity}</span>
                            {badge}
                          </div>
                        </div>
                        <div className="font-mono font-bold text-slate-900">€{(c.amount || 0).toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Visual Media Audit Vault — global lightbox */}
      <ImageLightbox
        open={!!lightbox.src}
        src={lightbox.src}
        caption={lightbox.caption}
        onClose={() => setLightbox({ src: null, caption: "" })}
      />
    </div>
  );
}

function Bucket({ title, icon, empty, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
        {icon}
        <span>{title}</span>
      </div>
      {empty ? (
        <div className="text-xs text-slate-400 italic px-3 py-2 border border-dashed border-slate-200 rounded-lg">Empty</div>
      ) : (
        <div className="space-y-1.5">{children}</div>
      )}
    </div>
  );
}
